import React from 'react';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Authentication method option type
 */
export type AuthMethodType = 'managed_key' | 'pkey' | 'password';

/**
 * Authentication option definition
 */
export interface AuthenticationOption {
  value: AuthMethodType;
  title: string;
  description: string;
  benefits: React.ReactNode;
}

/**
 * Props for the {@link AuthenticationMethodSelector} component
 */
interface AuthenticationMethodSelectorProps {
  /** Currently selected authentication method */
  selectedAuthMethod: AuthenticationOption;
  /** Callback when authentication method changes */
  onChange: (option: AuthenticationOption) => void;
}

/**
 * Available authentication options with detailed descriptions and benefits
 */
export const AUTHENTICATION_OPTIONS: AuthenticationOption[] = [
  {
    value: 'managed_key',
    title: '🔐 Managed Key-Based Authentication (Recommended)',
    description:
      'Let DataOps securely generate and manage your authentication keys. This provides the security of key-based authentication without the complexity of manual key management.',
    benefits: (
      <div className="space-y-3">
        <div>
          <p className="mb-2">🔒 Enterprise-Grade Security</p>
          <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
            <li>Keys encrypted with AES-256 in our secure database</li>
            <li>Private keys never leave our servers or shown to anyone</li>
            <li>Full audit logging of all key operations</li>
            <li>SOC 2 Type II certified infrastructure</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    value: 'pkey',
    title: '🔑 Bring Your Own Private Key (Advanced)',
    description:
      'Use a private key you have generated externally for authentication. You maintain full control over key generation and management.',
    benefits: (
      <div className="space-y-3">
        <div>
          <p className="mb-2">✓ When to Use</p>
          <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
            <li>Advanced security requirements</li>
            <li>Compliance mandates external key generation</li>
            <li>You already have keys from other tools</li>
            <li>Maximum control over cryptographic material</li>
          </ul>
        </div>
        <div>
          <p className="mb-2">🔑 You Control</p>
          <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
            <li>Key generation process and tools</li>
            <li>Key storage before upload</li>
            <li>Decision about key rotation</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    value: 'password',
    title: '🔓 Password Authentication (Development Only)',
    description:
      'Connect using your Snowflake username and password. Simple to set up but requires storing credentials.',
    benefits: (
      <div className="space-y-3">
        <div>
          <p className="mb-2">✓ When to Use</p>
          <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
            <li>Quick testing or development</li>
            <li>Temporary connections</li>
            <li>Learning DataOps features</li>
          </ul>
        </div>
        <div>
          <p className="mb-2">⚠️ Limitations</p>
          <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
            <li>Not recommended for production workloads</li>
            <li>Less secure than key-based methods</li>
            <li>May require frequent MFA codes</li>
          </ul>
        </div>
      </div>
    ),
  },
];

/**
 * AuthenticationMethodSelector Component
 *
 * Provides a Headless UI Listbox for selecting authentication method with three options:
 * Managed Key (recommended), BYOK, and Password. Includes detailed benefits display
 * for each method with Framer Motion animations.
 *
 * @component
 * @example
 * ```tsx
 * const [selected, setSelected] = useState(AUTHENTICATION_OPTIONS[0]);
 *
 * <AuthenticationMethodSelector
 *   selectedAuthMethod={selected}
 *   onChange={setSelected}
 * />
 * ```
 *
 * @param props - Component props
 * @param props.selectedAuthMethod - Currently selected authentication option
 * @param props.onChange - Callback when selection changes
 * @returns Authentication method selector with benefits display
 */
const AuthenticationMethodSelector: React.FC<AuthenticationMethodSelectorProps> = ({
  selectedAuthMethod,
  onChange,
}) => {
  return (
    <div>
      <h2 className="text-lg  text-dataops-dark">Authentication</h2>
      <p className="mt-1 text-sm text-secondary-text">Choose an authentication method for your Snowflake connection.</p>

      <Listbox value={selectedAuthMethod} onChange={onChange}>
        <Label className="sr-only">Change authentication method</Label>
        <motion.div
          className="relative mt-4"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          <ListboxButton
            className="w-full flex divide-x divide-dataops-secondary-blue rounded-md outline-none cursor-pointer hover:bg-hover-secondary-blue focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dataops-primary-blue transition-colors duration-150 shadow-sm"
            aria-label={`Selected authentication method: ${selectedAuthMethod.title}. Click to change.`}
          >
            <div className="flex items-center gap-x-2 rounded-l-md bg-dataops-secondary-blue px-4 py-2.5 text-white flex-1 min-w-0">
              <CheckIcon aria-hidden="true" className="h-5 w-5 flex-shrink-0" />
              <p className="text-sm font-medium break-words whitespace-normal">{selectedAuthMethod.title}</p>
            </div>
            <div className="flex items-center rounded-l-none rounded-r-md bg-dataops-secondary-blue px-3 py-2.5 flex-shrink-0">
              <span className="sr-only">Change authentication method</span>
              <ChevronDownIcon aria-hidden="true" className="h-5 w-5 text-white" />
            </div>
          </ListboxButton>

          <ListboxOptions
            modal={false}
            transition
            className="absolute left-0 right-0 z-10 mt-2 max-w-full sm:max-w-xl origin-top divide-y divide-gray-200 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in focus:outline-none"
          >
            {AUTHENTICATION_OPTIONS.map((option, index) => (
              <ListboxOption
                key={option.value}
                value={option}
                className="group cursor-pointer select-none p-4 text-sm text-primary-text hover:bg-dataops-primary-blue/10 data-[focus]:bg-dataops-primary-blue data-[focus]:text-white transition-colors duration-150"
              >
                {({ selected }) => (
                  <motion.div
                    className="flex flex-col"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <p
                        className={`font-medium ${
                          selected ? 'text-dataops-primary-blue group-data-[focus]:text-white' : ''
                        }`}
                      >
                        {option.title}
                      </p>
                      {selected && (
                        <CheckIcon
                          aria-label="Currently selected"
                          className="h-5 w-5 flex-shrink-0 text-dataops-primary-blue group-data-[focus]:text-white"
                        />
                      )}
                    </div>
                    <p className="mt-2 text-secondary-text group-data-[focus]:text-white/90">{option.description}</p>
                  </motion.div>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </motion.div>
      </Listbox>

      {/* Benefits Section */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedAuthMethod.value}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="mt-6"
          role="region"
          aria-label={`Benefits of ${selectedAuthMethod.title}`}
          aria-live="polite"
        >
          <div className="text-primary-text">{selectedAuthMethod.benefits}</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AuthenticationMethodSelector;
